/**
 * Catena d'identita' verso il BFF Limulo (`app-qlash-api`), ISOLATA dal resto.
 *
 * Il token che questa app usa con Limulo NON e' un token Logto: e' un JWT **interno**,
 * emesso dal BFF in cambio di un token esterno (`POST /qlash-api/v1/auth/exchange-token`,
 * `mobile-auth.js:800-841` @ `cabc619`). Vive in una chiave sua, non si mescola mai con
 * i token Logto per-servizio di `src/lib/logto.ts`, e quel file in fase 1 non si tocca.
 *
 * ⚠️ TRE COSE CHE VALE LA PENA SAPERE PRIMA DI LEGGERE IL CODICE
 *
 * 1. **Niente `import.meta.env` qui dentro.** Tutto — `fetch`, l'URL di base, l'orologio,
 *    l'identita' a cui legare la sessione — arriva come parametro. Serve perche' questo
 *    modulo e' esercitato da `tools/verify-limulo.ts` sotto Node, dove `import.meta.env`
 *    non esiste; ma il risultato e' che la catena d'identita' e' provabile senza rete,
 *    senza token e senza browser, che e' esattamente cio' che serve quando il percorso
 *    vero e' bloccato da un interruttore su un backoffice altrui.
 *
 * 2. **Il JWT interno e' un token di BACKOFFICE** (stesso segreto e stesso issuer del
 *    gestionale, `signGestionaleToken` a `mobile-auth.js:567-578`). Non e' un dettaglio
 *    di implementazione: significa che va trattato come una credenziale piena, non come
 *    un token di sola lettura di una sezione.
 *
 * 3. **La sessione porta scritto a chi appartiene** (`boundTo`) e la sua chiave e'
 *    inclusa nel teardown centrale `PER_SERVICE_TOKEN_KEYS` di `logto.ts`. Sono due
 *    difese complementari: il logout/cambio account la elimina subito; se quel teardown
 *    non gira, `readStoredSession` scarta comunque una sessione legata a un altro `sub`.
 */
import { limuloFailure, type LimuloFailure } from './errors.ts';
// ⚠️ `fetchWithTimeout.ts` non legge `import.meta.env`: importarlo qui non rompe il patto di
// questo modulo (esercitabile sotto Node, dipendenze come parametri).
import { TokenFetchTimeoutError } from '../fetchWithTimeout.ts';

/** Chiave dedicata: il token di Limulo non condivide storage con nessun altro servizio. */
export const LIMULO_STORAGE_KEY = 'qlash.limulo.session';

/** Margine con cui si considera «gia' scaduto» un token che sta per scadere. */
const REFRESH_SKEW_MS = 60_000;

export interface LimuloSession {
  /** Il JWT interno. Il server lo chiama `token`, non `accessToken`. */
  token: string;
  tokenType: string;
  /** Epoch ms, oppure `null` quando il server ha mandato una durata illeggibile. */
  expiresAt: number | null;
  refreshToken: string;
  refreshExpiresAt: number | null;
  organizationId: string;
  /** Il `sub` Logto a cui questa sessione e' legata. Vedi la nota 3 in testa al file. */
  boundTo: string;
}

export interface LimuloStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/**
 * Il minimo che serve a questo modulo di una risposta HTTP. `fetch` lo soddisfa; un
 * finto da tre righe pure — che e' il punto.
 */
export interface LimuloHttpResponse {
  status: number;
  json(): Promise<unknown>;
}

export interface LimuloAuthDeps {
  fetch: (url: string, init: Record<string, unknown>) => Promise<LimuloHttpResponse>;
  /** `/limulo-api` in dev (proxy Vite), l'origine di staging altrove. */
  baseUrl: string;
  now: () => number;
  /** Il `sub` Logto corrente, se noto: la sessione ci resta legata. */
  boundTo?: string;
  /**
   * L'ObjectId dell'organizzazione, per il PRIMO provisioning.
   *
   * ⚠️ Viaggia SU UNA ROTTA SOLA — l'exchange. Sul router principale l'organizzazione la
   * porta gia' il JWT interno, e un header discordante produce un 403 «The authenticated
   * organization does not match the active membership» (`organization-context.ts:61-70`):
   * un TERZO significato di 403 sopra quelli che gia' ci confondono. Non mandarlo dove non
   * serve non e' prudenza, e' l'unico modo di non fabbricare ambiguita'.
   */
  organizationId?: string;
}

export type LimuloAuthResult =
  | { ok: true; session: LimuloSession }
  | { ok: false; failure: LimuloFailure };

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * `expiresIn` arriva come STRINGA (`mobile-auth.js:577`: e' `config.jwtExpiresIn` passato
 * tale e quale, es. `'30d'`). Si rispecchia esattamente la grammatica che il server stesso
 * sa rileggere — `^(\d+)\s*([smhd])?$`, numero nudo = millisecondi (`mobile-auth.js:34-45`).
 *
 * Accettare piu' di lui sarebbe peggio che accettare meno: significherebbe fidarsi di una
 * durata che il server non saprebbe nemmeno interpretare. Cio' che non rientra torna
 * `null` — «non lo so», mai una scadenza inventata.
 */
export function parseJwtDuration(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d+)\s*([smhd])?$/);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return null;
  switch (match[2]) {
    case 's':
      return amount * 1000;
    case 'm':
      return amount * 60 * 1000;
    case 'h':
      return amount * 60 * 60 * 1000;
    case 'd':
      return amount * 24 * 60 * 60 * 1000;
    default:
      return amount;
  }
}

const absoluteOrDuration = (
  isoDate: unknown,
  duration: unknown,
  now: number,
): number | null => {
  // La data assoluta vince sulla durata: e' quella che il server ha davvero scritto nel
  // documento di sessione (`mobile-auth.js:710`), mentre la durata e' solo la sua
  // configurazione. Se la data e' illeggibile si cade sulla durata; se non lo e'
  // nemmeno quella, `null` — e `null` significa «non lo so», non «non scade mai».
  const parsed = Date.parse(asString(isoDate));
  if (Number.isFinite(parsed)) return parsed;
  const ms = parseJwtDuration(duration);
  return ms === null ? null : now + ms;
};

/**
 * Costruisce la sessione dall'envelope di `exchange-token` / `refresh-token`:
 * `{ ok, organizationId, data: { token, tokenType, expiresIn, refreshToken,
 * refreshExpiresIn, session, user, membership }, meta }`.
 *
 * Torna `null` — mai una sessione a meta' — quando manca il token: un `Authorization:
 * Bearer ` vuoto produrrebbe un 401 che sembra un problema di permessi.
 */
export function sessionFromAuthResponse(
  body: unknown,
  opts: { now: number; boundTo: string },
): LimuloSession | null {
  const envelope = asRecord(body);
  if (envelope.ok === false) return null;
  const data = asRecord(envelope.data);

  const token = asString(data.token);
  if (!token) return null;

  const expiresInMs = parseJwtDuration(data.expiresIn);
  const membership = asRecord(data.membership);
  const session = asRecord(data.session);

  return {
    token,
    tokenType: asString(data.tokenType) || 'Bearer',
    expiresAt: expiresInMs === null ? null : opts.now + expiresInMs,
    refreshToken: asString(data.refreshToken),
    refreshExpiresAt: absoluteOrDuration(
      session.refreshExpiresAt,
      data.refreshExpiresIn,
      opts.now,
    ),
    organizationId: asString(membership.organizationId) || asString(envelope.organizationId),
    boundTo: opts.boundTo,
  };
}

const postJson = async (
  path: string,
  payload: Record<string, unknown>,
  deps: LimuloAuthDeps,
  extraHeaders: Record<string, string> = {},
): Promise<LimuloAuthResult> => {
  let response: LimuloHttpResponse;
  try {
    response = await deps.fetch(`${deps.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...extraHeaders },
      // La sessione mobile e' materiale del BFF: i cookie del backoffice non c'entrano
      // nulla e mandarli allargherebbe la superficie senza servire a niente.
      credentials: 'omit',
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Una richiesta che non parte non e' un errore da propagare a una schermata: e' un
    // verdetto come gli altri.
    //
    // 🔴 E QUI SI PERDEVA LA SCADENZA. Prima questo `catch` appiattiva QUALUNQUE rigetto su
    // `FETCH_ERROR`, quindi una richiesta abbandonata dalla nostra scadenza diventava
    // indistinguibile da una rete caduta — e a valle, senza token, dal 401 del server, cioe'
    // da un rifiuto di IDENTITA'. All'utente si finiva per dire "rifai l'accesso" per una
    // richiesta che bastava riprovare.
    // ⇒ Il nome si conserva dal primo confine, che e' questo. Ripararlo piu' a valle avrebbe
    // voluto dire indovinare la causa da uno stato che non la porta piu'.
    const scaduta = error instanceof TokenFetchTimeoutError;
    return {
      ok: false,
      failure: limuloFailure({
        status: scaduta ? 'TIMEOUT_ERROR' : 'FETCH_ERROR',
        error: String(error),
      }),
    };
  }

  // Un corpo non-JSON (l'HTML di un 502 davanti al BFF) non deve far esplodere l'auth:
  // resta lo status, che e' gia' abbastanza per un verdetto onesto.
  const parsed = await response.json().catch(() => undefined);

  if (response.status >= 200 && response.status < 300) {
    const session = sessionFromAuthResponse(parsed, {
      now: deps.now(),
      boundTo: deps.boundTo ?? '',
    });
    if (session) return { ok: true, session };
    // ADR-0020: una risposta che contraddice il contratto e' un FINDING, non una cosa da
    // assorbire in silenzio. Non si inventa una sessione da un 2xx che non ne porta una.
    return {
      ok: false,
      failure: {
        ...limuloFailure({ status: response.status, data: parsed }),
        verdict: 'unknown',
        message: 'Risposta 2xx senza una sessione utilizzabile',
      },
    };
  }

  return { ok: false, failure: limuloFailure({ status: response.status, data: parsed }) };
};

const missingField = (message: string): LimuloAuthResult => ({
  ok: false,
  failure: limuloFailure({
    status: 400,
    data: { ok: false, error: message, code: 'E_QLASH_HTTP_400' },
  }),
});

/**
 * `POST /v1/auth/exchange-token` — token esterno (issuer STAGING
 * `stg-account.qlash.gg/oidc`, aud `api.tournaments.qlash.gg`, ES384) → JWT interno.
 *
 * Il token esterno viaggia nel CORPO e mai in query string: un token nell'URL finisce nei
 * log del reverse proxy, nei referrer e nella cronologia del browser.
 */
export function exchangeExternalToken(
  externalToken: string,
  deps: LimuloAuthDeps,
  identityToken?: string,
): Promise<LimuloAuthResult> {
  const token = asString(externalToken).trim();
  // Senza token la risposta la conosciamo gia' (400 `externalToken richiesto`): sparare
  // comunque la richiesta e' solo rumore su una rotta d'autenticazione.
  if (!token) return Promise.resolve(missingField('externalToken richiesto'));
  // L'header si mette solo se c'e' un valore: un `X-AM-ORGANIZATION: ` vuoto sarebbe un
  // contesto dichiarato e non fornito, cioe' peggio di nessun contesto.
  const orgId = asString(deps.organizationId).trim();
  const identity = asString(identityToken).trim();
  return postJson(
    '/v1/auth/exchange-token',
    identity
      ? { externalToken: token, identityToken: identity }
      : { externalToken: token },
    deps,
    orgId ? { 'X-AM-ORGANIZATION': orgId } : {},
  );
}

/**
 * Un rifiuto del refresh e' TERMINALE o TRANSITORIO, e confonderli costa la sessione.
 *
 * 🔴 Qui prima c'era `if (!result.ok) clearStoredSession()` — **qualunque** `!ok`. E
 * `postJson` (`auth.ts:176-199`) restituisce `ok: false` anche per una richiesta che **non
 * parte** (`status: null`, la WebView messa in background a meta' chiamata) e per **ogni**
 * non-2xx: 503, 502, un gateway davanti al BFF che risponde HTML.
 * ⇒ **Un singolo fallimento transitorio distruggeva una sessione sana**, e lo faceva in
 * silenzio: nessun `invalid_grant`, e nessuna riga nel registro del bridge, perche' questo
 * percorso non passa da `logto.ts`.
 *
 * 📌 *E' la stessa classe che `logto.ts` ha corretto dall'altro lato dell'auth — un rifiuto
 * recuperabile trattato come definitivo. Averla corretta li' non l'aveva corretta qui: due
 * lati, due implementazioni, e la lezione non ha attraversato il confine.*
 *
 * ⚠️ Il verso in cui si sbaglia NON e' simmetrico, ed e' la ragione della soglia:
 * · tenere una sessione morta ⇒ qualche 401, e la schermata degrada onestamente
 *   (`errors.ts` esiste per questo);
 * · buttare una sessione viva ⇒ l'utente rifa' il login, e perde cio' che stava facendo.
 */
export const isTerminalRefusal = (failure: { status: number | null }): boolean => {
  // Rete mai arrivata: non sappiamo NIENTE del refresh token. Non e' un rifiuto.
  if (failure.status === null) return false;
  // 5xx: e' il server ad avere un problema, non la nostra credenziale.
  if (failure.status >= 500) return false;
  // Tutto il resto (401, 403, 400, 422...) e' il server che ha guardato il token e ha
  // detto no: ritentare con lo stesso materiale non puo' cambiare esito.
  return true;
};

/**
 * `POST /v1/auth/refresh-token`.
 *
 * ⚠️ Nessun `X-AM-ORGANIZATION` qui: il refresh parte da una sessione che ESISTE gia', e
 * l'organizzazione e' gia' decisa dal token che stiamo rinnovando. Rimandarla puo' solo
 * contraddirla.
 *
 * ⚠️ IL REFRESH RUOTA. Il server rigenera `refreshTokenHash` a ogni giro
 * (`mobile-auth.js:707-710`): tenersi quello vecchio significa che il refresh SUCCESSIVO
 * fallisce. La sessione che esce da qui porta sempre il refresh nuovo.
 */
export function refreshLimuloSession(
  refreshToken: string,
  deps: LimuloAuthDeps,
): Promise<LimuloAuthResult> {
  const token = asString(refreshToken).trim();
  if (!token) return Promise.resolve(missingField('refreshToken richiesto'));
  return postJson('/v1/auth/refresh-token', { refreshToken: token }, deps);
}

const isUsableSession = (value: unknown): value is LimuloSession => {
  const session = asRecord(value);
  return asString(session.token).length > 0;
};

/**
 * Rilegge la sessione salvata, scartandola quando non e' piu' utilizzabile. Le tre
 * ragioni per scartarla, in ordine di importanza:
 *
 *  1. **e' di un altro utente** (`boundTo` diverso) — vedi la nota 3 in testa al file;
 *  2. il refresh e' scaduto: non c'e' modo di tornare a un token valido;
 *  3. il contenuto non e' rileggibile.
 *
 * Un access token scaduto NON e' una ragione: finche' il refresh vale, la sessione e' il
 * materiale con cui si ottiene il token nuovo, e buttarla manderebbe al login un utente
 * che non ha nessun bisogno di rifarlo.
 */
export function readStoredSession(
  storage: LimuloStorage,
  opts: { now: number; boundTo: string },
): LimuloSession | null {
  const raw = storage.get(LIMULO_STORAGE_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.remove(LIMULO_STORAGE_KEY);
    return null;
  }

  if (!isUsableSession(parsed)) {
    storage.remove(LIMULO_STORAGE_KEY);
    return null;
  }

  if (parsed.boundTo !== opts.boundTo) {
    storage.remove(LIMULO_STORAGE_KEY);
    return null;
  }

  if (parsed.refreshExpiresAt !== null && parsed.refreshExpiresAt <= opts.now) {
    storage.remove(LIMULO_STORAGE_KEY);
    return null;
  }

  return parsed;
}

export function writeStoredSession(storage: LimuloStorage, session: LimuloSession): void {
  storage.set(LIMULO_STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession(storage: LimuloStorage): void {
  storage.remove(LIMULO_STORAGE_KEY);
}

/**
 * Se conviene rinfrescare PRIMA di partire con una chiamata.
 *
 * Scadenza sconosciuta ⇒ `false`, di proposito: rinfrescare «per sicurezza» a ogni
 * chiamata raddoppierebbe il traffico su una rotta che scrive nel database a ogni giro.
 * Quando non si sa, si manda il token che si ha e si reagisce all'eventuale 401.
 */
export function needsRefresh(session: LimuloSession | null, now: number): boolean {
  if (!session || session.expiresAt === null) return false;
  return session.expiresAt - now <= REFRESH_SKEW_MS;
}

/**
 * IL SEMAFORO DELLA CATENA D'IDENTITA'.
 *
 * Ad app `qlash-api` spenta non si ottiene nessuna sessione — ma l'esito dell'exchange
 * dice comunque se la catena d'identita' regge, perche' la verifica del token esterno e
 * la risoluzione dell'organizzazione girano PRIMA del gate (`mobile-auth.js:495/506`,
 * poi `:822`). La scala, misurata su staging il 2026-08-30:
 *
 *   corpo vuoto            → 400  `externalToken richiesto`          → manca il token
 *   token presente e finto → 401  `E_QLASH_EXTERNAL_TOKEN_INVALID`   → catena ROTTA
 *   token valido           → 403  `Qlash API non attiva per il tenant` → catena OK
 *
 * ⚠️ Quindi, ad app spenta, **il 403 e' il risultato POSITIVO**. Senza dirlo, chi guarda
 * la schermata vede un errore rosso e conclude che non funziona nulla, proprio mentre
 * funziona tutto tranne un interruttore su un backoffice.
 */
export type ExchangeDiagnosis =
  | 'ok'
  | 'identity-chain-ok'
  | 'external-token-rejected'
  | 'identity-provider-down'
  | 'email-not-verified'
  | 'missing-token'
  | 'unknown';

export function diagnoseExchange(result: LimuloAuthResult): ExchangeDiagnosis {
  if (result.ok) return 'ok';
  switch (result.failure.verdict) {
    case 'app-off':
    case 'capability-off':
      return 'identity-chain-ok';
    case 'external-token-invalid':
      return 'external-token-rejected';
    case 'identity-provider-down':
      return 'identity-provider-down';
    case 'email-not-verified':
      return 'email-not-verified';
    case 'bad-request':
      return 'missing-token';
    default:
      return 'unknown';
  }
}


/**
 * Cosa ricordare dell'ultimo tentativo di sessione: il fallimento, o niente se e' andata bene.
 *
 * 🔴 Tre righe, ed esistono perche' una MUTAZIONE VERDE le ha chieste. La memoria viveva
 * inline in `session.ts` — un modulo che sotto Node non gira (legge `env`) — quindi
 * sostituire l'azzeramento sul successo con un `if (!result.ok)` **passava tutti i cancelli**.
 * E quella mutazione non e' innocua: un fallimento vecchio che sopravvive a un successo
 * spiegherebbe un 401 NUOVO con una causa vecchia, cioe' racconterebbe di nuovo la causa
 * sbagliata — che e' precisamente il difetto che questa modifica chiude.
 * 📌 *Una mutazione verde accusa il cancello; qui accusava il POSTO in cui la regola viveva.*
 */
export function rememberSessionFailure(result: LimuloAuthResult): LimuloFailure | null {
  return result.ok ? null : result.failure;
}
